import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { STATES } from "@/lib/format";

export function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="grain relative hidden flex-col justify-between overflow-hidden bg-[#0F172A] p-12 text-white lg:flex">
        <div className="flex items-center gap-2"><div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-600"><Landmark className="h-5 w-5" /></div><span className="text-lg font-bold">BharatLedger</span></div>
        <div className="max-w-md">
          <h2 className="text-4xl font-bold leading-tight">Invoicing, stock and GST — one calm ledger for your business.</h2>
          <p className="mt-4 text-slate-300">Server-calculated totals, immutable finalized documents, weighted-average costing, receivables and payables in INR paise precision.</p>
          <div className="mt-8 grid grid-cols-3 gap-3 text-xs text-slate-400">
            {[["GST", "CGST / SGST / IGST auto split"], ["WAC", "Snapshotted COGS"], ["Audit", "Every mutation logged"]].map(([k, v]) => (
              <div key={k} className="rounded-md border border-slate-800 bg-slate-900/60 p-3"><div className="num text-sm font-semibold text-white">{k}</div>{v}</div>))}
          </div>
        </div>
        <p className="text-xs text-slate-500">Operational tooling — not statutory accounting or GST filing software.</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="page-enter w-full max-w-md">
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{title}</h1><p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          <div className="mt-8 space-y-4">{children}</div>
          
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { applySession } = useAuth();
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await api.post("/auth/login", form); await applySession(data.data); toast.success("Welcome back"); navigate("/"); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title="Sign in" subtitle="Access your organization's ledger." footer={<>New business? <Link to="/register" className="font-semibold text-blue-600" data-testid="go-register-link">Create an organization</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" /></Field>
        <Field label="Password"><Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" /></Field>
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
    </AuthLayout>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "", state_code: "27" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { applySession } = useAuth();
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await api.post("/auth/register", form); await applySession(data.data); toast.success("Organization created"); navigate("/settings"); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <AuthLayout title="Create your organization" subtitle="You become the owner. Invite your team afterwards." footer={<>Already registered? <Link to="/login" className="font-semibold text-blue-600" data-testid="go-login-link">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name"><Input required value={form.name} onChange={set("name")} data-testid="register-name-input" /></Field>
          <Field label="Email"><Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" /></Field>
        </div>
        <Field label="Password" hint="Minimum 8 characters"><Input type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="register-password-input" /></Field>
        <Field label="Business name"><Input required value={form.org_name} onChange={set("org_name")} data-testid="register-org-input" /></Field>
        <Field label="Business state (for GST place of supply)">
          <select className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.state_code} onChange={set("state_code")} data-testid="register-state-select">
            {STATES.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
          </select>
        </Field>
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-button">{busy ? "Creating…" : "Create organization"}</Button>
      </form>
    </AuthLayout>
  );
}
